import styled, { ThemeColor } from 'styled-components';
import { FC } from 'react';
import { EdnaWrapper } from '@providers/EdnaProvider';
import { TYPOGRAPHY } from '@junglescout/styles-library';
import { getColor } from '../../helpers/colors';
import { TextProps, Variant } from './types';

const validVariants = Object.keys(TYPOGRAPHY);

const Component = styled((props) => <props.as {...props} />)<
  TextProps & { $color: string | ThemeColor }
>`
  /** Keep the variant typography styles at the top of this styled component so that other styles can override if need be. */
  ${(props: TextProps) => TYPOGRAPHY[props.variant as Variant]}

  /** Rest of styles */
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  text-align: ${(props) => props.align};
  text-decoration: ${(props) => (props.underline ? 'underline' : 'none')};
  font-weight: ${(props) => props.fontWeight};
  color: ${({ $color, theme: { colors } }) => getColor($color, colors)};
  ${(props) => props.uppercase && `text-transform: uppercase;`}
`;

/**
 * A utility component to render text.
 * Pass in a name from our Typography into the variant prop to render with preset styles. Use the `element` prop to determine what element you would like it to render as.
 * Read more in our docs [here](https://www.notion.so/junglescout/Text-Component-Typography-794ff90de29c4603bd4a54cbe1f48a2b?pvs=4).
 *
 * ```jsx
 * <Text variant="heading4xl" element="h1" align="center" color="primary">
 *  Hello World
 * </Text>
 * ```
 *
 */
const Text: FC<TextProps> = ({
  element = 'span',
  margin = '0',
  padding = '0',
  align = 'left',
  children,
  variant = 'bodySm',
  underline = false,
  color = 'grey900',
  fontWeight,
  uppercase = false,
  ...props
}) => {
  if (!validVariants.includes(variant)) {
    // eslint-disable-next-line no-console
    console.warn(
      `Invalid variant: ${variant}, please use one of: ${validVariants.join(
        ', '
      )}`
    );
  }

  return (
    <EdnaWrapper>
      <Component
        as={element}
        margin={margin}
        padding={padding}
        align={align}
        variant={variant}
        underline={underline}
        fontWeight={fontWeight}
        $color={color}
        uppercase={uppercase}
        {...props}
      >
        {children}
      </Component>
    </EdnaWrapper>
  );
};
Text.displayName = 'Text';

export default Text;
